import React from "react";
/**
 * Format number according to Dutch convention:
 * - Dot (.) for thousand separator: 100000 → 100.000
 * - Comma (,) for decimal separator: 10.5 → 10,5
 *
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted string with Dutch number convention
 */
export const formatDutchNumber = (value, decimals = 0) => {
    if (isNaN(value))
        return "";
    // Format with specified decimal places
    const fixed = value.toFixed(decimals);
    // Split into integer and decimal parts
    const parts = fixed.split(".");
    const integerPart = parts[0];
    const decimalPart = parts[1];
    // Add thousand separators (dots) to integer part
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    // Combine with comma for decimal separator if there are decimals
    if (decimalPart && parseInt(decimalPart) !== 0) {
        return `${formattedInteger},${decimalPart}`;
    }
    return formattedInteger;
};
/**
 * Format number with K/M suffixes using Dutch convention
 * Examples: 1500 → "2K" (rounded), 2000000 → "2M", 1500000 → "1,5M"
 * K values are rounded to whole numbers, M values may have decimals
 *
 * @param value - The number to format
 * @returns Formatted string with K/M suffix and Dutch number convention
 */
export const formatDutchNumberWithSuffix = (value) => {
    if (isNaN(value))
        return "";
    const absValue = Math.abs(value);
    // Always use M for values >= 1,000,000 (never show as K)
    if (absValue >= 1000000) {
        const millions = absValue / 1000000;
        const hasDecimals = absValue % 1000000 !== 0;
        const formatted = formatDutchNumber(millions, hasDecimals ? 1 : 0);
        return `${formatted}M`;
    }
    // Use K for values >= 1,000 but < 1,000,000 (rounded to whole numbers)
    else if (absValue >= 1000) {
        const thousands = absValue / 1000;
        // Round to nearest whole number
        const rounded = Math.round(thousands);
        const formatted = formatDutchNumber(rounded, 0);
        return `${formatted}`;
    }
    // No suffix for values < 1,000
    else {
        return formatDutchNumber(absValue, 0);
    }
};
/**
 * Format number with K suffix only (no M) using Dutch convention
 * Examples: 1500 → "2K", 2000000 → "2000K", 1500000 → "1500K", 346100 → "346K"
 * Values are rounded to whole numbers (no decimals)
 *
 * @param value - The number to format
 * @returns Formatted string with K suffix only and Dutch number convention
 */
export const formatDutchNumberWithKSuffixOnly = (value) => {
    if (isNaN(value))
        return "";
    const absValue = Math.abs(value);
    // Always use K for values >= 1,000 (convert millions to thousands)
    if (absValue >= 1000) {
        const thousands = absValue / 1000;
        // Round to nearest whole number
        const rounded = Math.round(thousands);
        const formatted = formatDutchNumber(rounded, 0);
        return `${formatted}`;
    }
    // No suffix for values < 1,000
    else {
        return formatDutchNumber(absValue, 0);
    }
};
/**
 * Convert numbers in a string from English format to Dutch format
 * Converts: "10,000.50" → "10.000,50" or "1,234" → "1.234"
 *
 * @param text - The text string that may contain English-formatted numbers
 * @returns Text string with numbers converted to Dutch format
 */
export const convertTooltipToDutchFormat = (text) => {
    if (!text)
        return "";
    // Pattern to match numbers with English formatting:
    // - Optional negative sign
    // - Digits with commas as thousand separators
    // - Optional decimal point followed by digits
    // This regex matches numbers like: 1,234.56 or 10,000 or 123.45
    return text.replace(/(-?\d{1,3}(?:,\d{3})*(?:\.\d+)?)/g, (match) => {
        // Remove commas (thousand separators) and keep the decimal point
        const withoutCommas = match.replace(/,/g, "");
        // Split by decimal point if it exists
        const parts = withoutCommas.split(".");
        if (parts.length === 2) {
            // Has decimal part: convert dot to comma and add thousand separators (dots)
            const integerPart = parts[0];
            const decimalPart = parts[1];
            const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
            return `${formattedInteger},${decimalPart}`;
        }
        else {
            // No decimal part: just add thousand separators (dots)
            return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        }
    });
};
/**
 * Convert tooltip text to Dutch format and add line breaks after each value
 * Splits by common delimiters (comma, semicolon, pipe) and formats each value
 *
 * @param text - The tooltip text string
 * @returns React element with formatted tooltip and line breaks
 */
export const formatTooltipWithLineBreaks = (text) => {
    if (!text)
        return "";
    // First convert numbers to Dutch format
    const dutchFormatted = convertTooltipToDutchFormat(text);
    // Split by common delimiters (comma, semicolon, pipe, or newline)
    // Try different delimiters in order of likelihood
    let parts = [];
    if (dutchFormatted.includes(";")) {
        parts = dutchFormatted.split(";").map(p => p.trim()).filter(p => p);
    }
    else if (dutchFormatted.includes("|")) {
        parts = dutchFormatted.split("|").map(p => p.trim()).filter(p => p);
    }
    else if (dutchFormatted.includes(",") && dutchFormatted.match(/,\s*[A-Za-z]/)) {
        // Comma followed by letter (likely delimiter, not decimal)
        parts = dutchFormatted.split(/,\s*(?=[A-Za-z])/).map(p => p.trim()).filter(p => p);
    }
    else if (dutchFormatted.includes("\n")) {
        parts = dutchFormatted.split("\n").map(p => p.trim()).filter(p => p);
    }
    else {
        // No clear delimiter found, return as single line
        return dutchFormatted;
    }
    // Return JSX with line breaks using React.createElement
    return React.createElement(React.Fragment, null, ...parts.map((part, index) => React.createElement(React.Fragment, { key: index }, part, index < parts.length - 1 && React.createElement("br"))));
};
